<template>
  <div class="cc-services-products">
    <CreateCompanyRadioCardGroup
      v-model="offerTypeModel"
      name="cc-offer-type"
      legend="What do you offer?"
      :options="offerTypeOptions"
    />

    <CreateCompanyWizardPanel
      title="Inventory Management"
      icon="bi bi-boxes"
      :expanded="enableInventoryModel"
    >
      <div class="cc-wizard-panel__toggle">
        <LabelCheckboxField
          input-id="cc-enable-inventory"
          label="Enable Inventory Tracking"
          v-model="enableInventoryModel"
          flex
        />
        <p class="cc-wizard-panel__help">
          Monitor stock levels, enable multiple measurements, and track inventory activities
        </p>
      </div>
      <template v-if="enableInventoryModel" #expanded>
        <div class="cc-services-products__expanded-grid">
          <LabelCheckboxField
            input-id="cc-multiple-warehouses"
            label="Have multiple warehouses?"
            v-model="multipleWarehousesModel"
            flex
          />
          <LabelCheckboxField
            input-id="cc-enable-units"
            label="Enable Units"
            v-model="enableUnitsModel"
            flex
          />
          <LabelDropdown
            class="cc-services-products__scheme"
            label="Inventory Scheme"
            select-id="cc-inventory-scheme"
            v-model="inventorySchemeModel"
            flex
            wide
            :options="inventorySchemeOptions"
          />
        </div>
      </template>
    </CreateCompanyWizardPanel>

    <CreateCompanyWizardPanel
      title="Tax Settings"
      icon="bi bi-percent"
      :expanded="enableTaxModel"
    >
      <div class="cc-wizard-panel__toggle">
        <LabelCheckboxField
          input-id="cc-enable-tax"
          label="Charge or Track Tax"
          v-model="enableTaxModel"
          flex
        />
        <p class="cc-wizard-panel__help">
          Configure sales tax, VAT, service tax, or other types of taxes
        </p>
      </div>
      <template v-if="enableTaxModel" #expanded>
        <div class="cc-services-products__tax-expanded">
          <fieldset class="cc-services-products__tax-fieldset">
            <legend class="cc-services-products__tax-legend">Tax item in transactions</legend>
            <div class="cc-services-products__tax-radios" role="radiogroup">
              <label
                v-for="opt in taxItemTypeOptions"
                :key="opt.value"
                class="cc-services-products__tax-radio"
                :class="{ 'cc-services-products__tax-radio--selected': taxItemTypeModel === opt.value }"
              >
                <input
                  type="radio"
                  name="cc-tax-item-type"
                  :value="opt.value"
                  :checked="taxItemTypeModel === opt.value"
                  @change="taxItemTypeModel = opt.value"
                />
                <span>{{ opt.label }}</span>
              </label>
            </div>
          </fieldset>
          <LabelCheckboxField
            input-id="cc-track-tax-paid"
            label="Enable Tracking Tax Paid"
            v-model="trackTaxPaidModel"
            flex
          />
        </div>
      </template>
    </CreateCompanyWizardPanel>
  </div>
</template>

<script>
import './create-company-services-products.css';
import CreateCompanyRadioCardGroup from './CreateCompanyRadioCardGroup.vue';
import CreateCompanyWizardPanel from './CreateCompanyWizardPanel.vue';
import LabelCheckboxField from '@/modules/consultant/components/LabelCheckboxField.vue';
import LabelDropdown from '@/modules/consultant/components/LabelDropdown.vue';
import {
  CREATE_COMPANY_OFFER_TYPE_OPTIONS,
  CREATE_COMPANY_INVENTORY_SCHEME_OPTIONS,
  CREATE_COMPANY_TAX_ITEM_TYPE_OPTIONS,
} from '@/constants/createCompanyFormOptions';

export default {
  name: 'CreateCompanyServicesProductsSetup',
  components: {
    CreateCompanyRadioCardGroup,
    CreateCompanyWizardPanel,
    LabelCheckboxField,
    LabelDropdown,
  },
  props: {
    offerType: { type: String, default: '' },
    enableInventory: { type: Boolean, default: false },
    multipleWarehouses: { type: Boolean, default: false },
    enableUnits: { type: Boolean, default: false },
    inventoryScheme: { type: String, default: 'average' },
    enableTax: { type: Boolean, default: false },
    taxItemType: { type: String, default: 'transaction' },
    trackTaxPaid: { type: Boolean, default: false },
  },
  emits: [
    'update:offerType',
    'update:enableInventory',
    'update:multipleWarehouses',
    'update:enableUnits',
    'update:inventoryScheme',
    'update:enableTax',
    'update:taxItemType',
    'update:trackTaxPaid',
  ],
  data() {
    return {
      offerTypeOptions: CREATE_COMPANY_OFFER_TYPE_OPTIONS,
      inventorySchemeOptions: CREATE_COMPANY_INVENTORY_SCHEME_OPTIONS,
      taxItemTypeOptions: CREATE_COMPANY_TAX_ITEM_TYPE_OPTIONS,
    };
  },
  computed: {
    offerTypeModel: {
      get() { return this.offerType; },
      set(v) { this.$emit('update:offerType', v); },
    },
    enableInventoryModel: {
      get() { return this.enableInventory; },
      set(v) { this.$emit('update:enableInventory', v); },
    },
    multipleWarehousesModel: {
      get() { return this.multipleWarehouses; },
      set(v) { this.$emit('update:multipleWarehouses', v); },
    },
    enableUnitsModel: {
      get() { return this.enableUnits; },
      set(v) { this.$emit('update:enableUnits', v); },
    },
    inventorySchemeModel: {
      get() { return this.inventoryScheme; },
      set(v) { this.$emit('update:inventoryScheme', v); },
    },
    enableTaxModel: {
      get() { return this.enableTax; },
      set(v) { this.$emit('update:enableTax', v); },
    },
    taxItemTypeModel: {
      get() { return this.taxItemType; },
      set(v) { this.$emit('update:taxItemType', v); },
    },
    trackTaxPaidModel: {
      get() { return this.trackTaxPaid; },
      set(v) { this.$emit('update:trackTaxPaid', v); },
    },
  },
};
</script>
